import ShareButton from "./_ShareButton";
import type { VisualPayload } from "../../types";
import { isSafeUrl } from "../../lib/safeUrl";

interface CardShellProps {
  title: string;
  source: string;
  timestamp: string;
  children: React.ReactNode;
  accent?: string;
  /**
   * Optional URL for the data source's homepage / source page.  When
   * provided, `source` renders as a clickable link in the footer
   * (good-faith attribution).  Cards with multiple sources or custom
   * footer markup (e.g. the ScrapedDuck cards that need dual
   * LeekDuck + ScrapedDuck credit) should leave this unset and
   * render their own footer instead.
   */
  sourceUrl?: string;
  /**
   * Optional path-only share URL (e.g. "/p/swampert").  When present,
   * a "Share" button renders in the footer that copies the absolute
   * URL to clipboard (or invokes the Web Share API on mobile).
   */
  shareUrl?: string;
  /**
   * Phase C — when shareUrl is empty but this is set, ShareButton
   * switches to snapshot mode: POST the full VisualPayload to /share,
   * copy the returned /s/<token> URL.  Time-sensitive cards
   * (raids today, cups today, etc.) opt in via
   * VisualPayload.snapshot_eligible.
   */
  snapshotPayload?: VisualPayload;
}

/** Shared card layout wrapper used by all card components. */
export default function CardShell({
  title, source, timestamp, children,
  accent = "border-white/[0.08]",
  sourceUrl, shareUrl, snapshotPayload,
}: CardShellProps) {
  const date = timestamp ? new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const hasFooter = source || date || shareUrl || snapshotPayload;
  return (
    <article
      data-testid="visual-card"
      aria-label={title}
      className={`bg-scout-elevated rounded-2xl p-4 border ${accent} animate-fade-in`}
    >
      <h3 className="text-xs font-semibold text-white/40 uppercase tracking-widest mb-3" aria-hidden="true">{title}</h3>
      {children}
      {hasFooter && (
        <div className="mt-4 flex items-center justify-between gap-2 text-[11px] text-white/20">
          <p className="flex items-center gap-1.5 min-w-0">
            {source && (
              isSafeUrl(sourceUrl) ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white/50 transition-colors underline-offset-2 hover:underline truncate"
                >
                  {source}
                </a>
              ) : (
                <span className="truncate">{source}</span>
              )
            )}
            {source && date && <span aria-hidden="true">·</span>}
            {date && <span className="shrink-0">{date}</span>}
          </p>
          {(shareUrl || snapshotPayload) && (
            <ShareButton
              path={shareUrl ?? ""}
              title={title}
              snapshotPayload={shareUrl ? undefined : snapshotPayload}
            />
          )}
        </div>
      )}
    </article>
  );
}
